<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

import NumberSpinner from '@/shared/components/UI/NumberSpinner.vue';
import {
  addToCartClickSources,
  breakpoints,
  posthogFeatureFlags
} from '@/shared/constants/constants';
import { isFeatureFlagEnabled } from '@/shared/pinia/modules/posthog/feature-flags';
import { usePostHogStore } from '@/shared/pinia/modules/posthog/posthog.store';
import { useProductStore } from '@/shared/pinia/modules/product';
import { useProductQuantitiesStore } from '@/shared/pinia/modules/product-quantities';
import { formattedPrice } from '@/shared/utils/shopify/money-formatter';

import MainAddToCartButton from '../CartActions/MainAddToCartButton.vue';

const productStore = useProductStore();
const quantityStore = useProductQuantitiesStore();

const isMainButtonInView = ref(true);
const isMainButtonAboveView = ref(false);
const showWhileMainButtonBelow = ref(false);

const isVisible = computed(
  () =>
    !isMainButtonInView.value &&
    (isMainButtonAboveView.value || showWhileMainButtonBelow.value)
);

let observer: IntersectionObserver | null = null;

const isPurchasable = computed(() => productStore.purchasable);

const displayPrice = computed(() => {
  const price = productStore.price.unit;
  if (price === null) return '';
  return formattedPrice(price);
});

const compareAtPrice = computed(() => {
  const before = productStore.price.before;
  if (before === null || before === productStore.price.unit) return '';
  return formattedPrice(before);
});

const mobileQuery = window.matchMedia(`(max-width: ${breakpoints.md - 1}px)`);
let isFlagResolved = false;
let isFlagResolving = false;

// Only mobile visitors see the bar, so only they get the flag evaluated —
// evaluating it on desktop would log an exposure.
const resolveShowWhileBelow = async () => {
  if (!mobileQuery.matches || isFlagResolved || isFlagResolving) return;
  isFlagResolving = true;
  const flagValue = await usePostHogStore()
    .cacheFeatureFlag(posthogFeatureFlags.STICKY_ATC_OFFSCREEN)
    .finally(() => {
      isFlagResolving = false;
    });
  if (flagValue === undefined) return;
  isFlagResolved = true;
  showWhileMainButtonBelow.value = isFeatureFlagEnabled(flagValue);
};

onMounted(() => {
  const target = document.getElementById('js-product-cart-actions');
  if (!target) return;

  observer = new IntersectionObserver(
    ([entry]) => {
      isMainButtonInView.value = entry.isIntersecting;
      isMainButtonAboveView.value = entry.boundingClientRect.top < 0;
    },
    { threshold: 0 }
  );

  observer.observe(target);
  void resolveShowWhileBelow();
  mobileQuery.addEventListener('change', resolveShowWhileBelow);
});

onUnmounted(() => {
  observer?.disconnect();
  mobileQuery.removeEventListener('change', resolveShowWhileBelow);
});
</script>

<template>
  <Transition
    enter-active-class="transition-transform duration-200 ease-out"
    enter-from-class="translate-y-full"
    enter-to-class="translate-y-0"
    leave-active-class="transition-transform duration-150 ease-in"
    leave-from-class="translate-y-0"
    leave-to-class="translate-y-full"
  >
    <div
      v-if="isPurchasable && isVisible"
      class="fixed inset-x-0 bottom-0 z-sticky-atc border-t border-[#dfe4e6] bg-white px-4 pb-[env(safe-area-inset-bottom)] shadow-[0_4px_16px_rgba(26,63,79,0.25)] md:hidden"
    >
      <div class="flex min-h-[72px] items-center gap-4 py-3">
        <div class="flex min-w-0 flex-1 flex-col">
          <span
            class="text-h6 font-bold"
            :class="compareAtPrice ? 'text-pink' : 'text-[#1a3f4f]'"
          >
            {{ displayPrice }}
          </span>
          <span
            v-if="compareAtPrice"
            class="text-sm leading-5 text-[#a1aab1] line-through"
          >
            {{ compareAtPrice }}
          </span>
        </div>

        <NumberSpinner
          class="shrink-0"
          size="compact"
          wrapper-class="!h-10 !rounded-md !border-[#ced4d9] !px-0"
          button-class="!h-10 !w-9 !rounded-none disabled:text-[#a1aab1]"
          input-class="!text-base !font-bold"
          :model-value="quantityStore.quantity"
          @update:model-value="quantityStore.updateQuantity"
        />

        <MainAddToCartButton
          class="shrink-0"
          compact
          prevent-animation
          :source="addToCartClickSources.STICKY"
        />
      </div>
    </div>
  </Transition>
</template>
